'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { normalizeSlug } from '@goodneighbor/utilities/slug';
import { useSlugAvailability } from '@/hooks/useSlugAvailability';
import type { PartnerInterestFormValues } from './partnerRegistrationSchema';

type SlugStatusType = 'checking' | 'available' | 'taken';

type SlugStatusInfo = {
  message: string;
  type: SlugStatusType;
} | null;

type UsePartnerSlugOptions = {
  form: UseFormReturn<PartnerInterestFormValues>;
  debounceMs?: number;
};

export default function usePartnerSlug({
  form,
  debounceMs = 400
}: UsePartnerSlugOptions) {
  const { slug, setSlug, isChecking, isAvailable, isTaken } =
    useSlugAvailability();

  const lastAutoSlugRef = useRef<string>('');
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const currentFormSlug = form.getValues('slug');
    if (slug !== currentFormSlug) {
      form.setValue('slug', slug, { shouldValidate: slug.length >= 3 });
    }
  }, [slug, form]);

  useEffect(() => {
    return () => {
      if (slugDebounceRef.current) {
        clearTimeout(slugDebounceRef.current);
      }
    };
  }, []);

  const handleOrganizationNameChange = useCallback(
    (name: string, onChange: (value: string) => void) => {
      onChange(name);

      if (slugDebounceRef.current) {
        clearTimeout(slugDebounceRef.current);
      }

      slugDebounceRef.current = setTimeout(() => {
        const currentSlug = form.getValues('slug');
        if (!currentSlug || currentSlug === lastAutoSlugRef.current) {
          const newSlug = normalizeSlug(name);
          lastAutoSlugRef.current = newSlug;
          setSlug(newSlug);
        }
      }, debounceMs);
    },
    [form, setSlug, debounceMs]
  );

  const slugStatusInfo = useMemo<SlugStatusInfo>(() => {
    if (!slug || slug.length < 3) return null;
    if (isChecking) {
      return { message: 'Checking availability...', type: 'checking' };
    }
    if (isAvailable) {
      return { message: 'This URL is available!', type: 'available' };
    }
    if (isTaken) {
      return {
        message: 'This URL is already taken. Please choose a different one.',
        type: 'taken'
      };
    }
    return null;
  }, [slug, isChecking, isAvailable, isTaken]);

  return {
    slug,
    setSlug,
    isChecking,
    isAvailable,
    isTaken,
    slugStatusInfo,
    handleOrganizationNameChange
  };
}
